import React, { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { validateSubdomain } from "./validateSubdomain";
import PortalNotFound from "./PortalNotFound";
import PortalInactive from "./PortalInactive";
import { useResolvePortalQuery } from "../../../store/apiSlice/portalAuthApiSlice";
import { setPortalResolved, setPortalResolveFailed } from "../../../store/slice/portalContextSlice";
import {
  hydratePortalSessions,
  setActivePortalSubdomain,
  portalLogout,
} from "../../../store/slice/portalAuthSlice";
import { loadPortalSessions } from "../../../store/slice/portalAuthUtils";
import { Spin } from "antd";

/**
 * PortalBootstrap
 *
 * Shared gate for ALL portal routes (auth + private).
 * Responsibilities:
 *   1. Validate subdomain format → show PortalNotFound if invalid
 *   2. Resolve portal metadata from backend → store in Redux
 *   3. Hydrate portal sessions from localStorage into Redux
 *   4. Listen for cross-tab logouts via localStorage events
 *   5. Gate inactive portals → show PortalInactive
 *   6. Inject portal branding CSS variables
 *
 * Does NOT attempt token refresh — that belongs in PortalPrivateRoutes
 * so auth pages (login, forgot-password, etc.) are never blocked by
 * a pointless refresh call against an empty cookie jar.
 */
const PortalBootstrap = () => {
  const { subdomain } = useParams();
  const dispatch = useDispatch();

  const isValidFormat = validateSubdomain(subdomain).ok;

  // 2. Hydrate portal sessions from localStorage into Redux on mount
  useEffect(() => {
    const sessions = loadPortalSessions();
    dispatch(hydratePortalSessions(sessions));
  }, [dispatch]);

  // 3. Listen for cross-tab logouts written by portalLogout action
  useEffect(() => {
    const handleStorageChange = e => {
      if (e.key === `portalAuthOut:${subdomain}` && e.newValue !== null) {
        dispatch(portalLogout({ subdomain, isCrossTab: true }));
        localStorage.removeItem(`portalAuthOut:${subdomain}`);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [subdomain, dispatch]);

  // 4. Resolve portal metadata (skipped if subdomain is invalid)
  const {
    data: portalData,
    error: resolveError,
    isLoading: isResolving,
  } = useResolvePortalQuery(subdomain, {
    skip: !isValidFormat,
  });

  // 5. Persist resolved portal context into Redux
  useEffect(() => {
    if (portalData) {
      dispatch(setPortalResolved({ subdomain, data: portalData }));
      dispatch(setActivePortalSubdomain(subdomain));
    } else if (resolveError) {
      dispatch(setPortalResolveFailed({ subdomain, error: resolveError }));
    }
  }, [portalData, resolveError, subdomain, dispatch]);

  // --- Render gates ---

  // Invalid subdomain format
  if (!isValidFormat) {
    return <PortalNotFound />;
  }

  // Backend returned 404 — subdomain doesn't map to any portal
  if (resolveError?.status === 404) {
    return <PortalNotFound />;
  }

  // Network failure or unexpected backend error (e.g. FETCH_ERROR, 500)
  if (resolveError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: "12px",
        }}
      >
        <p style={{ color: "#4A4A68", fontFamily: "Inter", fontSize: "16px" }}>
          Unable to connect. Please check your connection and try again.
        </p>
      </div>
    );
  }

  // Waiting for resolve API
  if (isResolving) {
    return <PortalSpinner size="large" />;
  }

  // Portal exists but is inactive
  if (portalData && !portalData.is_active) {
    return <PortalInactive name={portalData.name} />;
  }

  // Inject portal primary color as a CSS variable for themed buttons/links
  const rootStyle = portalData?.primary_color ? { "--Primary": portalData.primary_color } : {};

  return (
    <div style={rootStyle}>
      <Outlet context={{ portalData }} />
    </div>
  );
};

export default PortalBootstrap;

export const PortalSpinner = ({ ...props }) => {
  return (
    <div
      style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
    >
      <Spin {...props} />
    </div>
  );
};
